/**
 * Champs techniques configurables (CDC §8.2).
 *
 * « Les champs techniques sont configurables et typés. » Rien n'est codé en dur :
 * c'est ici que le client déclare que ses compresseurs ont une pression de
 * service en bars. Sans cet écran, il fallait passer par moi — ce qui vidait
 * l'exigence de son sens.
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';

import { ApiError, api, type AttributeDataType, type AttributeDefinition } from '../api/client';
import { Champ, ErreurFormulaire, Ligne, Panneau } from '../composants/Formulaire';

const TYPES: Record<AttributeDataType, { texte: string; aide: string }> = {
  TEXT: { texte: 'Texte libre', aide: 'Une chaîne quelconque.' },
  NUMBER: { texte: 'Nombre', aide: 'Accepte la virgule décimale française.' },
  BOOLEAN: { texte: 'Oui / Non', aide: 'Deux valeurs seulement.' },
  DATE: { texte: 'Date', aide: 'Enregistrée au format année-mois-jour.' },
  ENUM: { texte: 'Liste de choix', aide: 'Vous fixez les valeurs autorisées.' },
};

export function AttributeDefinitionsPage() {
  const [definitions, setDefinitions] = useState<AttributeDefinition[] | null>(null);
  const [familles, setFamilles] = useState<string[]>([]);
  const [erreur, setErreur] = useState<ApiError | Error | null>(null);
  const [succes, setSucces] = useState<string | null>(null);
  const [formulaireOuvert, setFormulaireOuvert] = useState(false);
  const [enEdition, setEnEdition] = useState<string | null>(null);
  const [familleEdition, setFamilleEdition] = useState('');
  const [attenduEdition, setAttenduEdition] = useState(false);
  const [enregistrementEnCours, setEnregistrementEnCours] = useState(false);

  const charger = useCallback(async () => {
    try {
      setDefinitions(await api.attributeDefinitions());
      setErreur(null);
    } catch (e) {
      setErreur(e instanceof ApiError ? e : new Error('Chargement impossible.'));
    }
  }, []);

  useEffect(() => {
    void charger();
    void api
      .assets({ take: 500 })
      .then((tout) => setFamilles([...new Set(tout.items.map((m) => m.assetType))].sort()))
      .catch(() => undefined);
  }, [charger]);

  const installerJeuDeDepart = async () => {
    setErreur(null);
    try {
      const r = await api.seedAttributeDefinitions();
      setSucces(
        r.created === 0
          ? 'Le jeu de départ est déjà installé, rien n’a été ajouté.'
          : `${r.created} champ${r.created > 1 ? 's' : ''} ajouté${r.created > 1 ? 's' : ''}.`,
      );
      await charger();
    } catch (e) {
      setErreur(e instanceof ApiError ? e : new Error('Installation impossible.'));
    }
  };

  const ouvrirEdition = (d: AttributeDefinition) => {
    setEnEdition(d.id);
    setFamilleEdition(d.appliesToAssetType ?? '');
    setAttenduEdition(d.expected);
    setSucces(null);
  };

  const enregistrerEdition = async (id: string) => {
    setEnregistrementEnCours(true);
    setErreur(null);
    try {
      await api.updateAttributeDefinition(id, {
        appliesToAssetType: familleEdition.trim() || null,
        expected: attenduEdition,
      });
      setEnEdition(null);
      await charger();
    } catch (e) {
      setErreur(e instanceof ApiError ? e : new Error('Enregistrement impossible.'));
    } finally {
      setEnregistrementEnCours(false);
    }
  };

  return (
    <>
      <div className="entete-page">
        <div>
          <h1>Champs techniques</h1>
          <p>
            Les caractéristiques que vos machines portent. Aucune n’est imposée : vous déclarez les
            vôtres.
          </p>
        </div>
        <div className="actions">
          <button className="secondaire" onClick={() => void installerJeuDeDepart()}>
            Installer le jeu de départ
          </button>
          <button onClick={() => setFormulaireOuvert(true)}>Nouveau champ</button>
        </div>
      </div>

      <ErreurFormulaire
        message={erreur?.message ?? null}
        details={erreur instanceof ApiError ? erreur.details : undefined}
      />
      {succes && <div className="message succes">{succes}</div>}

      <div className="carte">
        {definitions === null ? (
          <div className="vide">Chargement…</div>
        ) : definitions.length === 0 ? (
          <div className="vide">
            Aucun champ technique.
            <br />
            « Installer le jeu de départ » propose quelques caractéristiques courantes, que vous
            pourrez ensuite compléter.
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Caractéristique</th>
                <th style={{ width: 150 }}>Type</th>
                <th style={{ width: 90 }}>Unité</th>
                <th>S’applique à</th>
                <th style={{ width: 110 }}>Attendu</th>
                <th style={{ width: 220 }} />
              </tr>
            </thead>
            <tbody>
              {definitions.map((d) => (
                <tr key={d.id}>
                  <td>
                    <strong>{d.label}</strong>
                    <div className="aide">
                      <code>{d.code}</code>
                    </div>
                  </td>
                  <td>
                    {TYPES[d.dataType].texte}
                    {d.dataType === 'ENUM' && d.options.length > 0 && (
                      <div className="aide">{d.options.join(', ')}</div>
                    )}
                  </td>
                  <td>{d.unit ?? <span className="aide">—</span>}</td>
                  <td>
                    {enEdition === d.id ? (
                      <input
                        type="text"
                        list="familles-champ-edition"
                        placeholder="Toutes les familles"
                        value={familleEdition}
                        onChange={(e) => setFamilleEdition(e.target.value)}
                        aria-label={`Famille de machines pour ${d.label}`}
                      />
                    ) : (
                      d.appliesToAssetType ?? <span className="aide">toutes les familles</span>
                    )}
                  </td>
                  <td>
                    {enEdition === d.id ? (
                      <label className="case">
                        <input
                          type="checkbox"
                          checked={attenduEdition}
                          onChange={(e) => setAttenduEdition(e.target.checked)}
                          aria-label={`Attendu pour ${d.label}`}
                        />
                      </label>
                    ) : d.expected ? (
                      <span className="etat attente">Attendu</span>
                    ) : (
                      <span className="aide">facultatif</span>
                    )}
                  </td>
                  <td className="actions">
                    {enEdition === d.id ? (
                      <>
                        <button
                          className="secondaire discret"
                          disabled={enregistrementEnCours}
                          onClick={() => void enregistrerEdition(d.id)}
                        >
                          {enregistrementEnCours ? 'Enregistrement…' : 'Enregistrer'}
                        </button>
                        <button
                          className="secondaire discret"
                          disabled={enregistrementEnCours}
                          onClick={() => setEnEdition(null)}
                        >
                          Annuler
                        </button>
                      </>
                    ) : (
                      <button className="secondaire discret" onClick={() => ouvrirEdition(d)}>
                        Modifier « s’applique à » / « attendu »
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <datalist id="familles-champ-edition">
          {familles.map((f) => (
            <option key={f} value={f} />
          ))}
        </datalist>
      </div>

      <div className="message info">
        Un champ <strong>attendu</strong> non renseigné est signalé sur la fiche machine, sans
        jamais bloquer : une donnée manquante se complète au fil des passages (CDC §8.2).
      </div>

      {formulaireOuvert && (
        <FormulaireDefinition
          familles={familles}
          onFermer={() => setFormulaireOuvert(false)}
          onEnregistre={() => {
            setSucces('Champ technique créé.');
            void charger();
          }}
        />
      )}
    </>
  );
}

/** Création d'un champ technique. */
function FormulaireDefinition({
  familles,
  onFermer,
  onEnregistre,
}: {
  familles: string[];
  onFermer: () => void;
  onEnregistre: () => void;
}) {
  const [label, setLabel] = useState('');
  const [code, setCode] = useState('');
  const [codeManuel, setCodeManuel] = useState(false);
  const [dataType, setDataType] = useState<AttributeDataType>('TEXT');
  const [unit, setUnit] = useState('');
  const [appliesTo, setAppliesTo] = useState('');
  const [options, setOptions] = useState('');
  const [expected, setExpected] = useState(false);
  const [erreur, setErreur] = useState<ApiError | Error | null>(null);
  const [enCours, setEnCours] = useState(false);

  /**
   * Le code est dérivé du libellé tant que l'utilisateur ne l'a pas touché.
   * Il sert de clé stable aux imports : le laisser saisir à la main dès le
   * départ produirait des codes incohérents sans bénéfice.
   */
  const changerLabel = (v: string) => {
    setLabel(v);
    if (!codeManuel) {
      setCode(
        v
          .normalize('NFD')
          .replace(/\p{Diacritic}/gu, '')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_+|_+$/g, '')
          .slice(0, 60),
      );
    }
  };

  const enregistrer = async (e: FormEvent) => {
    e.preventDefault();
    if (!label.trim() || !code.trim()) {
      setErreur(new Error('Le libellé et le code sont obligatoires.'));
      return;
    }
    const listeOptions = options
      .split(',')
      .map((o) => o.trim())
      .filter(Boolean);

    if (dataType === 'ENUM' && listeOptions.length === 0) {
      setErreur(new Error('Une liste de choix a besoin d’au moins une valeur autorisée.'));
      return;
    }

    setEnCours(true);
    setErreur(null);
    try {
      await api.createAttributeDefinition({
        code: code.trim(),
        label: label.trim(),
        dataType,
        unit: unit.trim() || undefined,
        appliesToAssetType: appliesTo.trim() || undefined,
        options: listeOptions,
        expected,
        position: 100,
      });
      onEnregistre();
      onFermer();
    } catch (e) {
      setErreur(e instanceof ApiError ? e : new Error('Enregistrement impossible.'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Panneau
      titre="Nouveau champ technique"
      sousTitre="Il apparaîtra sur les fiches des machines concernées."
      onFermer={onFermer}
      actions={
        <>
          <button className="secondaire" onClick={onFermer} disabled={enCours}>
            Annuler
          </button>
          <button onClick={(e) => void enregistrer(e)} disabled={enCours}>
            {enCours ? 'Enregistrement…' : 'Créer le champ'}
          </button>
        </>
      }
    >
      <form onSubmit={(e) => void enregistrer(e)}>
        <ErreurFormulaire
          message={erreur?.message ?? null}
          details={erreur instanceof ApiError ? erreur.details : undefined}
        />

        <Champ libelle="Libellé" requis aide="Ce que verront vos techniciens.">
          <input
            type="text"
            autoFocus
            placeholder="Débit d’air"
            value={label}
            onChange={(e) => changerLabel(e.target.value)}
          />
        </Champ>

        <Champ libelle="Code" requis aide="Clé stable, utilisée par les imports. Ne la changez plus ensuite.">
          <input
            type="text"
            value={code}
            onChange={(e) => {
              setCodeManuel(true);
              setCode(e.target.value);
            }}
          />
        </Champ>

        <Ligne>
          <Champ libelle="Type" aide={TYPES[dataType].aide}>
            <select
              value={dataType}
              onChange={(e) => setDataType(e.target.value as AttributeDataType)}
            >
              {(Object.keys(TYPES) as AttributeDataType[]).map((t) => (
                <option key={t} value={t}>
                  {TYPES[t].texte}
                </option>
              ))}
            </select>
          </Champ>
          <Champ libelle="Unité" aide="bar, kW, m³/h… libre.">
            <input type="text" value={unit} onChange={(e) => setUnit(e.target.value)} />
          </Champ>
        </Ligne>

        {dataType === 'ENUM' && (
          <Champ
            libelle="Valeurs autorisées"
            requis
            aide="Séparées par des virgules : R134a, R410A, R32"
          >
            <input type="text" value={options} onChange={(e) => setOptions(e.target.value)} />
          </Champ>
        )}

        <Champ
          libelle="Famille de machines"
          aide="Laissez vide pour appliquer à toutes les familles."
        >
          <input
            type="text"
            list="familles-champ"
            placeholder="Toutes les familles"
            value={appliesTo}
            onChange={(e) => setAppliesTo(e.target.value)}
          />
          <datalist id="familles-champ">
            {familles.map((f) => (
              <option key={f} value={f} />
            ))}
          </datalist>
        </Champ>

        <Champ libelle="Attendu">
          <label className="case">
            <input
              type="checkbox"
              checked={expected}
              onChange={(e) => setExpected(e.target.checked)}
            />
            Signaler son absence sur la fiche machine
          </label>
        </Champ>
      </form>
    </Panneau>
  );
}
